// Argus — store pentru modal-uri admin globale (NU per-agent ca windowStore).
// Ferestre globale au integrare proprie în WindowsTaskbar (secțiune Admin tools).
//
// Bloc msi-deployment refactor 2026-05-06: /admin/updates → floating window global.
// Z-order coordonat cu `windowStore.topZ` pentru ordonare unitară admin + agent windows.
// Persistence localStorage prin Zustand `persist` middleware (open + minimized + position).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useWindowStore } from './windowStore';

export interface AdminWindowState {
  open: boolean;
  minimized: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  zOrder: number;
}

interface AdminModalsState {
  updates: AdminWindowState;
  openUpdates: () => void;
  closeUpdates: () => void;
  minimizeUpdates: () => void;
  restoreUpdates: () => void;
  bringUpdatesToFront: () => void;
  setUpdatesPosition: (x: number, y: number) => void;

  // Bloc v1.0.10 #A + #D — modaluri Dialog (simpler decât floating window):
  // My profile, User permissions, Watched paths.
  myProfileOpen: boolean;
  openMyProfile: () => void;
  closeMyProfile: () => void;
  permissionsOpen: boolean;
  openPermissions: () => void;
  closePermissions: () => void;
  watchedPathsOpen: boolean;
  openWatchedPaths: () => void;
  closeWatchedPaths: () => void;
}

const DEFAULT_UPDATES_RECT = { x: 80, y: 80, width: 1100, height: 720 };

/**
 * Bumpează contorul partajat z-order din windowStore + returnează noul z.
 * Folosit de admin windows pentru a se ridica deasupra agent FloatingWindow-urilor (și invers).
 */
function bumpSharedTopZ(): number {
  const ws = useWindowStore.getState();
  const newZ = ws.topZ + 1;
  useWindowStore.setState({ topZ: newZ });
  return newZ;
}

export const useAdminModalsStore = create<AdminModalsState>()(
  persist(
    (set) => ({
      updates: { open: false, minimized: false, ...DEFAULT_UPDATES_RECT, zOrder: 1 },

      openUpdates: () => {
        const newZ = bumpSharedTopZ();
        set((s) => ({ updates: { ...s.updates, open: true, minimized: false, zOrder: newZ } }));
      },

      closeUpdates: () =>
        set((s) => ({ updates: { ...s.updates, open: false, minimized: false } })),

      minimizeUpdates: () =>
        set((s) => ({ updates: { ...s.updates, minimized: true } })),

      restoreUpdates: () => {
        const newZ = bumpSharedTopZ();
        set((s) => ({ updates: { ...s.updates, minimized: false, zOrder: newZ } }));
      },

      bringUpdatesToFront: () => {
        const newZ = bumpSharedTopZ();
        set((s) => ({ updates: { ...s.updates, zOrder: newZ } }));
      },

      setUpdatesPosition: (x, y) =>
        set((s) => ({ updates: { ...s.updates, x, y } })),

      myProfileOpen: false,
      openMyProfile: () => set({ myProfileOpen: true }),
      closeMyProfile: () => set({ myProfileOpen: false }),
      permissionsOpen: false,
      openPermissions: () => set({ permissionsOpen: true }),
      closePermissions: () => set({ permissionsOpen: false }),
      watchedPathsOpen: false,
      openWatchedPaths: () => set({ watchedPathsOpen: true }),
      closeWatchedPaths: () => set({ watchedPathsOpen: false }),
    }),
    {
      name: 'argus.adminModals',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist DOAR field-urile de UI state — NU action-urile.
        updates: {
          open: state.updates.open,
          minimized: state.updates.minimized,
          x: state.updates.x,
          y: state.updates.y,
          width: state.updates.width,
          height: state.updates.height,
          zOrder: 1,  // reset zOrder la load (re-bumpat la primul bringToFront)
        },
      }),
    }
  )
);
